import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { useSession } from "../auth/session";
import { credits } from "../lib/format";
import { useCredits, useRedeemCode } from "../state/queries";
import { useUiStore } from "../state/uiStore";
import { Badge, Button, Card, Field, Icon, TextInput } from "../ui";

// Account/Billing-Seite. Anmeldung = Supabase-Identität (E-Mail+Passwort, Verify-Link, D47).
// Plan-Seed aus §10.4/D53 (kanonisch in §9.14 billing_products; hier nur Anzeige). Checkout
// läuft über Paddle (D14) — die Webhook-/Checkout-Route ist §13-deferred; die Buttons zeigen
// den definierten „kommt"-Zustand.

const PLANS = [
  { id: "starter", name: "Starter", price: "$9", credits: 900 },
  { id: "pro", name: "Pro", price: "$29", credits: 2900, featured: true },
  { id: "studio", name: "Studio", price: "$79", credits: 7900 },
];

function SignInCard() {
  const openAuth = useUiStore((s) => s.openAuth);
  return (
    <Card className="max-w-md">
      <h2 className="mb-1 text-lg font-semibold">Anmelden</h2>
      <p className="mb-4 text-sm text-muted">
        Mit E-Mail & Passwort anmelden; neue Konten brauchen Name + E-Mail-Bestätigung (D47).
      </p>
      <Button variant="primary" onClick={openAuth}>
        Anmelden / Registrieren
      </Button>
    </Card>
  );
}

function PromoCard() {
  const redeem = useRedeemCode();
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      const res = await redeem.mutateAsync(code.trim());
      setMsg({ tone: "ok", text: `Eingelöst — neues Saldo: ${credits(res.available)}.` });
      setCode("");
    } catch (err) {
      // Backend liefert bereits deutsche Klartext-Meldungen (§9.15); Fallback generisch.
      const text = err instanceof ApiError ? err.message : "Code konnte nicht eingelöst werden.";
      setMsg({ tone: "err", text });
    }
  }

  return (
    <Card className="max-w-md">
      <h3 className="mb-1 font-semibold">Promo-Code</h3>
      <p className="mb-3 text-sm text-muted">Gutschein-Code einlösen — die Credits werden sofort gutgeschrieben.</p>
      <form onSubmit={onSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <Field label="Code" htmlFor="promo-code">
            <TextInput
              id="promo-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="z. B. WELCOME20"
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              disabled={redeem.isPending}
            />
          </Field>
        </div>
        <Button type="submit" variant="primary" disabled={redeem.isPending || !code.trim()}>
          {redeem.isPending ? "…" : "Einlösen"}
        </Button>
      </form>
      {msg && (
        <p
          role={msg.tone === "err" ? "alert" : "status"}
          aria-live="polite"
          className={msg.tone === "ok" ? "mt-3 text-sm text-accent" : "mt-3 text-sm text-danger"}
        >
          {msg.text}
        </p>
      )}
    </Card>
  );
}

function PlanCard({ plan }: { plan: (typeof PLANS)[number] }) {
  return (
    <Card className={plan.featured ? "border-accent/50" : undefined}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{plan.name}</h3>
        {plan.featured && <Badge tone="accent">beliebt</Badge>}
      </div>
      <div className="mt-2 text-2xl font-bold">
        {plan.price}
        <span className="text-sm font-normal text-muted">/Mo</span>
      </div>
      <div className="mt-1 text-sm text-muted">{credits(plan.credits)} / Monat</div>
      <Button variant={plan.featured ? "primary" : "secondary"} className="mt-4 w-full" disabled title="Checkout via Paddle — §13">
        Wählen
      </Button>
    </Card>
  );
}

export function Account() {
  const { status, user, signOut } = useSession();
  const balance = useCredits();

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Account</h1>
            <p className="text-sm text-muted">
              {status === "signed-in"
                ? (user?.email ?? "angemeldet")
                : status === "loading"
                  ? "…"
                  : status === "unconfigured"
                    ? "Supabase nicht konfiguriert (Dev) — VITE_SUPABASE_URL/ANON_KEY setzen."
                    : "nicht angemeldet"}
            </p>
          </div>
          {status === "signed-in" && (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5 font-mono text-sm">
                <Icon name="coins" size={16} className="text-accent" />
                {balance.data !== undefined ? credits(balance.data) : "—"}
              </span>
              <Button variant="secondary" onClick={() => void signOut()}>
                Abmelden
              </Button>
            </div>
          )}
        </header>

        {status !== "signed-in" && status !== "loading" && <SignInCard />}
        {status === "signed-in" && <PromoCard />}

        <section>
          <h2 className="mb-3 text-lg font-semibold">Pläne</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {PLANS.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}
          </div>
          <p className="mt-3 text-xs text-muted">
            1 Credit = $0,01 (D53). Top-up-Packs (2 J. gültig) & Free-Tier (20 cr) folgen. Verbrauch:
            earliest-expiry-first (D8/D44).
          </p>
        </section>

        <section>
          <h2 className="mb-3 text-lg font-semibold">API & MCP</h2>
          <Card className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted">
              API-Keys und die MCP-/Plugin-Installation findest du jetzt unter „MCP &amp; API".
            </p>
            <Link
              to="/mcp"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
            >
              Zu MCP &amp; API
              <Icon name="api" size={14} />
            </Link>
          </Card>
        </section>
      </div>
    </div>
  );
}
